import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import '../../component-styles/TagsSettings.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import ConfirmModal from '../utils/ConfirmModal';

const DEFAULT_COLOR = '#62794e';

export default function TagsSettings() {
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  const [tags, setTags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState({ name: '', color: DEFAULT_COLOR });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/tags/${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => notify('Failed to load tags'));
  }, [userId]);

  function openAdd() {
    setEditingTag(null);
    setForm({ name: '', color: DEFAULT_COLOR });
    setShowModal(true);
  }

  function openEdit(tag) {
    setEditingTag(tag);
    setForm({ name: tag.name, color: tag.color || DEFAULT_COLOR });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (editingTag) {
        const res = await fetch(`${API_BASE}/api/tags`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, oldName: editingTag.name, newName: name, color: form.color }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || 'Failed to update tag');
        }
        setTags((prev) =>
          prev.map((t) => (t.name === editingTag.name ? { ...t, name, color: form.color } : t))
        );
      } else {
        const res = await fetch(`${API_BASE}/api/tags`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, name, color: form.color }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.error || 'Failed to create tag');
        }
        setTags((prev) => [...prev, { userId, name, color: form.color }].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowModal(false);
    } catch (err) {
      notify(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(tag) {
    askConfirm({
      title: 'Delete Tag',
      message: `Permanently delete the tag "${tag.name}"? It will be removed from all items that use it.`,
      confirmLabel: 'Delete',
      destructive: true,
      action: async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/tags?userId=${encodeURIComponent(userId)}&name=${encodeURIComponent(tag.name)}`,
            { method: 'DELETE' }
          );
          if (!res.ok) throw new Error('Failed to delete tag');
          setTags((prev) => prev.filter((t) => t.name !== tag.name));
        } catch (err) {
          notify(err.message);
        }
      },
    });
  }

  return (
    <div className="tagset-container">
      <div className="tagset-header">
        <span className="tagset-section-title">Available Tags</span>
        <button className="tagset-add-btn" onClick={openAdd}>+ Add Tag</button>
      </div>

      <div className="tagset-list">
        {tags.length === 0 && (
          <span className="tagset-empty">No tags yet. Add one to get started.</span>
        )}
        {tags.map((tag) => (
          <span
            key={tag.name}
            className="tagset-chip"
            style={{ borderLeftColor: tag.color || DEFAULT_COLOR }}
          >
            <button
              className="tagset-chip-name"
              onClick={() => navigate(`/settings/tags/${encodeURIComponent(tag.name)}`)}
              title={`View all items tagged "${tag.name}"`}
            >
              {tag.name}
            </button>
            <span className="tagset-chip-actions">
              <button className="tagset-icon-btn" onClick={() => openEdit(tag)} title="Edit">
                <Pencil size={13} />
              </button>
              <button className="tagset-icon-btn delete" onClick={() => handleDelete(tag)} title="Delete">
                <Trash2 size={13} />
              </button>
            </span>
          </span>
        ))}
      </div>

      {showModal && (
        <div className="tagset-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="tagset-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingTag ? 'Edit Tag' : 'New Tag'}</h3>
            <form className="tagset-form" onSubmit={handleSubmit}>
              <label>Tag Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Priority, On Hold, Rush"
                autoFocus
                required
              />

              <label>Color</label>
              <div className="tagset-color-row">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                />
                <span
                  className="tagset-color-preview"
                  style={{ background: form.color }}
                />
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{form.color}</span>
              </div>

              <div className="tagset-actions">
                <button type="button" className="tagset-cancel-btn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tagset-save-btn" disabled={saving}>
                  {saving ? 'Saving...' : editingTag ? 'Save Changes' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
